import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Send,
  Users,
  MessageSquare,
  Eye,
  EyeOff,
  Loader2,
  UserPlus,
  Reply,
  ReplyAll,
  FileText,
  Clock,
  ChevronRight,
  Search,
  Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { AppLogo } from "@/components/AppLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  "Negócios": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Projetos": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Conteúdo": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Pessoal": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

export default function MessagesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user, token, isAuthenticated } = useAuth();
  
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowReceiving, setAllowReceiving] = useState(false);
  const [loadingVisibility, setLoadingVisibility] = useState(true);
  
  // Thread detail
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadInfo, setThreadInfo] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  
  // Reply
  const [replyText, setReplyText] = useState("");
  const [replyType, setReplyType] = useState("all");
  const [replyDirectTo, setReplyDirectTo] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Add participant
  const [showAddUser, setShowAddUser] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    fetchVisibility();
    fetchThreads();
  }, [isAuthenticated]);

  const fetchVisibility = async () => {
    try {
      const res = await axios.get(`${API}/user/visibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllowReceiving(res.data.allow_receiving);
    } catch { /* ignore */ } finally {
      setLoadingVisibility(false);
    }
  };

  const toggleVisibility = async () => {
    const newVal = !allowReceiving;
    setAllowReceiving(newVal);
    try {
      await axios.put(`${API}/user/visibility`, { allow_receiving: newVal }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(newVal ? "Agora outros usuários podem te enviar ideias!" : "Você não aparecerá mais para outros usuários.");
    } catch {
      setAllowReceiving(!newVal);
      toast.error("Erro ao atualizar visibilidade");
    }
  };

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(res.data.threads);
    } catch {
      toast.error("Erro ao carregar mensagens");
    } finally {
      setLoading(false);
    }
  };

  const openThread = async (threadId) => {
    setSelectedThread(threadId);
    setLoadingThread(true);
    setReplyText("");
    setReplyType("all");
    setReplyDirectTo(null);
    try {
      const res = await axios.get(`${API}/messages/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreadMessages(res.data.messages);
      setThreadInfo(res.data);
      // Update unread in list
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, unread_count: 0 } : t));
    } catch {
      toast.error("Erro ao carregar conversa");
      setSelectedThread(null);
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [threadMessages]);

  const handleReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/threads/${selectedThread}/reply`, {
        text: replyText,
        reply_type: replyType,
        direct_to: replyDirectTo,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReplyText("");
      setReplyType("all");
      setReplyDirectTo(null);
      // Reload thread
      await openThread(selectedThread);
    } catch {
      toast.error("Erro ao enviar resposta");
    } finally {
      setSending(false);
    }
  };

  // Search available users to add
  const searchAvailableUsers = useCallback(async (q) => {
    if (!q.trim()) {
      setAvailableUsers([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await axios.get(`${API}/users/available?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter out current participants
      const currentParts = threadInfo?.participants || [];
      setAvailableUsers(res.data.users.filter(u => !currentParts.includes(u.id)));
    } catch {
      setAvailableUsers([]);
    } finally {
      setSearchingUsers(false);
    }
  }, [token, threadInfo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAddUser) searchAvailableUsers(searchUser);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchUser, showAddUser, searchAvailableUsers]);

  const handleAddParticipant = async (userId) => {
    try {
      await axios.post(`${API}/messages/threads/${selectedThread}/add-participant`, {
        user_id: userId,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Participante adicionado!");
      setShowAddUser(false);
      setSearchUser("");
      await openThread(selectedThread);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao adicionar participante");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "agora";
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // Get initials from name
  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?";
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-300" style={{ background: 'var(--vi-page)' }} data-testid="messages-page">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2.5 md:px-8 lg:px-12 md:py-3" style={{ borderBottom: '1px solid var(--vi-border)' }}>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => selectedThread ? (setSelectedThread(null), fetchThreads()) : navigate("/")}
            className="h-9 w-9 rounded-full"
            style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}
            data-testid="messages-back-button"
          >
            <ArrowLeft className="w-4 h-4" style={{ color: 'var(--vi-text-soft)' }} />
          </Button>
          <h1 className="font-heading text-lg font-semibold" style={{ color: 'var(--vi-text)' }}>
            {selectedThread ? "Conversa" : "Mensagens"}
          </h1>
        </div>
        <AppLogo size="small" showText={false} />
      </header>

      {/* Visibility Toggle */}
      {!selectedThread && (
        <div className="px-4 md:px-8 lg:px-12 py-3" style={{ borderBottom: '1px solid var(--vi-border)' }}>
          <div className="flex items-center justify-between max-w-2xl mx-auto rounded-xl p-3" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
            <div className="flex items-center gap-3">
              {allowReceiving ? (
                <Eye className="w-5 h-5 text-emerald-400" />
              ) : (
                <EyeOff className="w-5 h-5" style={{ color: 'var(--vi-text-muted)' }} />
              )}
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--vi-text)' }}>
                  {allowReceiving ? "Visível para outros usuários" : "Invisível para outros usuários"}
                </p>
                <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                  {allowReceiving ? "Outros podem te enviar ideias" : "Ative para receber ideias de outros"}
                </p>
              </div>
            </div>
            <Switch
              checked={allowReceiving}
              onCheckedChange={toggleVisibility}
              disabled={loadingVisibility}
              data-testid="visibility-toggle"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 flex flex-col max-w-2xl mx-auto w-full">
        {!selectedThread ? (
          /* Thread List */
          <div className="flex-1 px-4 py-4 space-y-2" data-testid="threads-list">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Inbox className="w-12 h-12 mb-4" style={{ color: 'var(--vi-text-muted)', opacity: 0.3 }} />
                <p className="text-sm font-medium" style={{ color: 'var(--vi-text-muted)' }}>
                  Nenhuma mensagem ainda
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--vi-text-muted)', opacity: 0.7 }}>
                  Envie ideias para outros usuários pelo painel "Minhas Ideias"
                </p>
              </div>
            ) : (
              <>
                {threads.map((thread) => (
                  <button
                    key={thread.id}
                    onClick={() => openThread(thread.id)}
                    className="w-full text-left rounded-xl p-4 transition-all hover:scale-[1.01]"
                    style={{ background: 'var(--vi-input-bg)', border: `1px solid ${thread.unread_count > 0 ? 'rgba(99,102,241,0.3)' : 'var(--vi-border-accent)'}` }}
                    data-testid={`thread-item-${thread.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold bg-indigo-500/20 text-indigo-400">
                        {getInitials(thread.participants_info?.[0]?.name || "?")}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Names + time */}
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--vi-text)' }}>
                            {thread.participants_info?.map(p => p.name).join(", ") || "Conversa"}
                          </p>
                          <span className="text-xs shrink-0" style={{ color: 'var(--vi-text-muted)' }}>
                            {formatDate(thread.last_message_at)}
                          </span>
                        </div>
                        {/* Idea title */}
                        <p className="text-xs font-medium truncate mt-0.5" style={{ color: 'var(--vi-text-soft)' }}>
                          {thread.idea_title}
                        </p>
                        {/* Last message */}
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <p className="text-xs truncate" style={{ color: 'var(--vi-text-muted)' }}>
                            {thread.last_message?.sender_name ? `${thread.last_message.sender_name}: ` : ""}
                            {thread.last_message?.type === "share" ? (thread.last_message.text || "Enviou uma ideia") : thread.last_message?.text || ""}
                          </p>
                          {thread.unread_count > 0 && (
                            <span className="min-w-[20px] h-5 flex items-center justify-center text-[10px] font-bold rounded-full bg-indigo-500 text-white px-1.5 shrink-0" data-testid={`unread-badge-${thread.id}`}>
                              {thread.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 shrink-0 self-center" style={{ color: 'var(--vi-text-muted)' }} />
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        ) : (
          /* Thread Detail */
          <div className="flex-1 flex flex-col" data-testid="thread-detail">
            {loadingThread ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : threadInfo ? (
              <>
                {/* Thread Header */}
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--vi-border)' }}>
                  {/* Idea Card */}
                  <div className="rounded-lg p-3 mb-2" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-sm font-medium" style={{ color: 'var(--vi-text)' }}>{threadInfo.idea_title}</span>
                      {threadInfo.idea_category && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${CATEGORY_COLORS[threadInfo.idea_category] || CATEGORY_COLORS["Pessoal"]}`}>
                          {threadInfo.idea_category}
                        </span>
                      )}
                    </div>
                    <p className="text-xs line-clamp-2" style={{ color: 'var(--vi-text-muted)' }}>
                      {threadInfo.idea_content}
                    </p>
                  </div>
                  {/* Participants */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Users className="w-3.5 h-3.5" style={{ color: 'var(--vi-text-muted)' }} />
                    {threadInfo.participants_info?.map(p => (
                      <span key={p.id} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--vi-input-bg)', color: 'var(--vi-text-soft)', border: '1px solid var(--vi-border-accent)' }}>
                        {p.id === user?.id ? "Você" : p.name}
                      </span>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowAddUser(!showAddUser); setSearchUser(""); setAvailableUsers([]); }}
                      className="h-6 px-2 text-xs gap-1 text-indigo-400 hover:bg-indigo-500/10"
                      data-testid="add-participant-button"
                    >
                      <UserPlus className="w-3 h-3" />
                      Adicionar
                    </Button>
                  </div>
                  {/* Add Participant Search */}
                  <>
                    {showAddUser && (
                      <div className="mt-2 transition-all duration-200">

                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--vi-text-muted)' }} />
                          <Input
                            placeholder="Buscar usuário..."
                            value={searchUser}
                            onChange={(e) => setSearchUser(e.target.value)}
                            className="pl-9 h-8 text-xs"
                            style={{ background: 'var(--vi-card-solid)', borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text)' }}
                            data-testid="add-participant-search"
                          />
                        </div>
                        {searchingUsers && <Loader2 className="w-4 h-4 animate-spin text-indigo-500 mx-auto mt-2" />}
                        {availableUsers.length > 0 && (
                          <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                            {availableUsers.map(u => (
                              <button
                                key={u.id}
                                onClick={() => handleAddParticipant(u.id)}
                                className="w-full text-left px-3 py-1.5 rounded-lg text-xs flex items-center gap-2 hover:bg-indigo-500/10 transition-colors"
                                style={{ color: 'var(--vi-text-soft)' }}
                                data-testid={`add-user-${u.id}`}
                              >
                                <div className="w-6 h-6 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-semibold">
                                  {getInitials(u.name)}
                                </div>
                                {u.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {searchUser.trim() && !searchingUsers && availableUsers.length === 0 && (
                          <p className="text-[10px] text-center mt-2" style={{ color: 'var(--vi-text-muted)' }}>
                            Nenhum usuário encontrado
                          </p>
                        )}
                      </div>
                    )}
                  </>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ maxHeight: 'calc(100vh - 340px)' }} data-testid="messages-list">
                  {threadMessages.map((msg) => {
                    const isMe = msg.sender_id === user?.id;
                    const isSystem = msg.type === "system";
                    const isDirect = msg.type === "reply_direct";

                    if (isSystem) {
                      return (
                        <div key={msg.id} className="flex justify-center" data-testid={`msg-${msg.id}`}>
                          <span className="text-[10px] px-3 py-1 rounded-full" style={{ background: 'var(--vi-input-bg)', color: 'var(--vi-text-muted)' }}>
                            {msg.text}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMe ? "justify-end" : "justify-start"} animate-fade-in-up`}
                        data-testid={`msg-${msg.id}`}
                      >
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          isMe
                            ? "bg-indigo-600 text-white rounded-br-md"
                            : "rounded-bl-md"
                        }`}
                        style={!isMe ? { background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' } : {}}
                        >
                          {!isMe && (
                            <p className="text-[10px] font-semibold mb-1 text-indigo-400">
                              {msg.sender_name}
                            </p>
                          )}
                          {isDirect && (
                            <p className="text-[10px] mb-1 italic" style={{ color: isMe ? 'rgba(255,255,255,0.6)' : 'var(--vi-text-muted)' }}>
                              Mensagem privada
                            </p>
                          )}
                          {msg.type === "share" && (
                            <p className="text-[10px] mb-1 font-medium" style={{ color: isMe ? 'rgba(255,255,255,0.7)' : 'var(--vi-text-muted)' }}>
                              Compartilhou uma ideia
                            </p>
                          )}
                          <p className="text-sm leading-relaxed" style={{ color: isMe ? 'white' : 'var(--vi-text-soft)' }}>
                            {msg.text || (msg.type === "share" ? "Confira a ideia acima!" : "")}
                          </p>
                          <p className="text-[10px] mt-1 text-right" style={{ color: isMe ? 'rgba(255,255,255,0.5)' : 'var(--vi-text-muted)' }}>
                            {formatFullDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Bar */}
                <div className="px-4 py-3" style={{ borderTop: '1px solid var(--vi-border)' }}>
                  {/* Reply type selector */}
                  {threadInfo.participants?.length > 2 && (
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => { setReplyType("all"); setReplyDirectTo(null); }}
                        className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                          replyType === "all"
                            ? "bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
                            : "border-transparent"
                        }`}
                        style={replyType !== "all" ? { color: 'var(--vi-text-muted)' } : {}}
                        data-testid="reply-all-toggle"
                      >
                        <ReplyAll className="w-3 h-3 inline mr-1" />
                        Todos
                      </button>
                      {threadInfo.participants_info?.filter(p => p.id !== user?.id).map(p => (
                        <button
                          key={p.id}
                          onClick={() => { setReplyType("direct"); setReplyDirectTo(p.id); }}
                          className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                            replyType === "direct" && replyDirectTo === p.id
                              ? "bg-violet-500/20 text-violet-400 border-violet-500/30"
                              : "border-transparent"
                          }`}
                          style={!(replyType === "direct" && replyDirectTo === p.id) ? { color: 'var(--vi-text-muted)' } : {}}
                          data-testid={`reply-direct-${p.id}`}
                        >
                          <Reply className="w-3 h-3 inline mr-1" />
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={replyType === "direct" ? "Mensagem privada..." : "Responder..."}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                      className="flex-1 h-10"
                      style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text)' }}
                      data-testid="reply-input"
                    />
                    <Button
                      onClick={handleReply}
                      disabled={!replyText.trim() || sending}
                      className="h-10 w-10 shrink-0 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="reply-send-button"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
